import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from '@tanstack/react-router'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'

import { StyledEngineProvider, ThemeProvider, createTheme } from '@mui/material/styles'
import { LicenseInfo } from '@mui/x-license'

import { SnackBarProvider } from 'Common/components/snackbars/SnackbarToast'
import { logErrorToService } from 'Common/utils/logging/log'

import { router } from '../../router'
import GlobalError from '../error/GlobalError'
import mockingbirdTheme from '../styles/themes/mockingbirdBaseTheme'
import { getQueryClient } from './queryClient'

const initApp = () => {
    const theme = createTheme(mockingbirdTheme)
    const rootElement = document.getElementById('root')!
    const queryClient = getQueryClient()
    const handleError = (error: unknown) => {
        const root = createRoot(rootElement)
        root.render(<GlobalError />)
        throw error
    }

    LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY)

    try {
        const root = createRoot(rootElement)
        root.render(
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme} defaultMode="light">
                    <ErrorBoundary
                        onError={(error, { componentStack }) => {
                            // v6 types the caught error as unknown
                            const caught = error as Error & { cause?: unknown }
                            logErrorToService({
                                message: caught?.cause
                                    ? JSON.stringify(caught.cause)
                                    : (caught?.message ?? '-'),
                                stack: componentStack
                                    ? JSON.stringify(componentStack)
                                    : (caught?.stack ?? '-'),
                            })
                        }}
                        FallbackComponent={GlobalError}
                    >
                        <SnackBarProvider>
                            <QueryClientProvider client={queryClient}>
                                <ReactQueryDevtools
                                    initialIsOpen={false}
                                    buttonPosition="top-right"
                                />
                                <RouterProvider router={router} />
                            </QueryClientProvider>
                        </SnackBarProvider>
                    </ErrorBoundary>
                </ThemeProvider>
            </StyledEngineProvider>,
        )
    } catch (error) {
        handleError(error)
    }
}

export default initApp
